import React, { useState, useEffect } from 'react';
import {
  getPaymentSettingsAdmin,
  updateAdminEmail,
  createPaymentChannel,
  updatePaymentChannel,
  deletePaymentChannel
} from '../../api/paymentSettings';

const emptyChannelForm = {
  name: '',
  account_name: '',
  value: '',
  sort_order: '',
  is_active: true
};

const PaymentSettings = () => {
  const [adminEmail, setAdminEmail] = useState('');
  const [emailDraft, setEmailDraft] = useState('');
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingChannel, setSavingChannel] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [formData, setFormData] = useState(emptyChannelForm);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [channelToDelete, setChannelToDelete] = useState(null);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (!notification) return;
    const timer = setTimeout(() => setNotification(null), 3000);
    return () => clearTimeout(timer);
  }, [notification]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await getPaymentSettingsAdmin();
      setAdminEmail(data.adminEmail || '');
      setEmailDraft(data.adminEmail || '');
      setChannels(data.paymentChannels || []);
    } catch (error) {
      console.error('Error loading payment settings:', error);
      showNotification(error.message || 'Failed to load payment settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEmail = async (e) => {
    e.preventDefault();
    setSavingEmail(true);
    try {
      const result = await updateAdminEmail(emailDraft);
      setAdminEmail(result.adminEmail);
      setEmailDraft(result.adminEmail);
      showNotification('Proof-of-payment email updated');
    } catch (error) {
      showNotification(error.message || 'Failed to update email', 'error');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const openCreateForm = () => {
    setEditingChannel(null);
    setFormData({
      ...emptyChannelForm,
      sort_order: channels.length > 0
        ? Math.max(...channels.map(c => c.sort_order || 0)) + 1
        : 1
    });
    setShowForm(true);
  };

  const openEditForm = (channel) => {
    setEditingChannel(channel);
    setFormData({
      name: channel.name || '',
      account_name: channel.account_name || '',
      value: channel.value || '',
      sort_order: channel.sort_order ?? '',
      is_active: Boolean(channel.is_active)
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingChannel(null);
    setFormData(emptyChannelForm);
  };

  const handleChannelSubmit = async (e) => {
    e.preventDefault();
    setSavingChannel(true);
    try {
      const payload = {
        name: formData.name,
        account_name: formData.account_name,
        value: formData.value,
        sort_order: formData.sort_order === '' ? undefined : Number(formData.sort_order),
        is_active: formData.is_active
      };

      if (editingChannel) {
        await updatePaymentChannel(editingChannel.id, payload);
        showNotification('Payment channel updated');
      } else {
        await createPaymentChannel(payload);
        showNotification('Payment channel created');
      }

      closeForm();
      await loadSettings();
    } catch (error) {
      showNotification(error.message || 'Failed to save payment channel', 'error');
    } finally {
      setSavingChannel(false);
    }
  };

  const confirmDelete = async () => {
    if (!channelToDelete) return;
    try {
      await deletePaymentChannel(channelToDelete.id);
      showNotification('Payment channel deleted');
      setShowDeleteDialog(false);
      setChannelToDelete(null);
      await loadSettings();
    } catch (error) {
      showNotification(error.message || 'Failed to delete payment channel', 'error');
    }
  };

  if (loading) {
    return <div style={{ padding: 20, textAlign: 'center' }}>Loading payment settings...</div>;
  }

  return (
    <div style={{ padding: 20 }}>
      {notification && (
        <div style={{
          position: 'fixed',
          top: 20,
          right: 20,
          padding: '12px 24px',
          borderRadius: 6,
          color: '#fff',
          backgroundColor: notification.type === 'success' ? '#28a745' : '#dc3545',
          zIndex: 1000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          {notification.message}
        </div>
      )}

      <h2 style={{ color: '#2a3b5c', marginTop: 0, marginBottom: 8 }}>Payment Settings</h2>
      <p style={{ color: '#666', marginTop: 0, marginBottom: 24 }}>
        These details appear on the booking thank-you screen. Changes take effect immediately — no server restart needed.
      </p>

      <div style={{
        backgroundColor: '#f8f9fa',
        borderRadius: 8,
        padding: 20,
        marginBottom: 24
      }}>
        <h3 style={{ color: '#2a3b5c', marginTop: 0 }}>Proof of Payment Email</h3>
        <form onSubmit={handleSaveEmail} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#2a3b5c' }}>
              Email address
            </label>
            <input
              type="email"
              value={emailDraft}
              onChange={(e) => setEmailDraft(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: 4,
                boxSizing: 'border-box'
              }}
            />
          </div>
          <button
            type="submit"
            disabled={savingEmail || emailDraft.trim() === adminEmail}
            style={{
              backgroundColor: '#4f8cff',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '10px 20px',
              fontWeight: 600,
              cursor: savingEmail || emailDraft.trim() === adminEmail ? 'not-allowed' : 'pointer',
              opacity: savingEmail || emailDraft.trim() === adminEmail ? 0.6 : 1
            }}
          >
            {savingEmail ? 'Saving...' : 'Save Email'}
          </button>
        </form>
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16
      }}>
        <h3 style={{ color: '#2a3b5c', margin: 0 }}>Payment Channels</h3>
        <button
          onClick={openCreateForm}
          style={{
            backgroundColor: '#4f8cff',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '10px 20px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Add Channel
        </button>
      </div>

      <div style={{
        backgroundColor: '#fff',
        borderRadius: 8,
        border: '1px solid #eee',
        overflow: 'hidden'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Order</th>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Name</th>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Account Name</th>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Account / Number</th>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Status</th>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {channels.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ padding: 20, textAlign: 'center', color: '#666' }}>
                  No payment channels yet. Add one to show on the thank-you screen.
                </td>
              </tr>
            ) : (
              channels.map((channel) => (
                <tr key={channel.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: 12 }}>{channel.sort_order}</td>
                  <td style={{ padding: 12, fontWeight: 600, color: '#2a3b5c' }}>{channel.name}</td>
                  <td style={{ padding: 12 }}>{channel.account_name}</td>
                  <td style={{ padding: 12 }}>{channel.value}</td>
                  <td style={{ padding: 12 }}>
                    <span style={{
                      backgroundColor: channel.is_active ? '#28a745' : '#6c757d',
                      color: '#fff',
                      padding: '4px 8px',
                      borderRadius: 4,
                      fontSize: '0.8em',
                      fontWeight: 500
                    }}>
                      {channel.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: 12 }}>
                    <button
                      onClick={() => openEditForm(channel)}
                      style={{
                        marginRight: 8,
                        padding: '6px 12px',
                        backgroundColor: '#4f8cff',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        setChannelToDelete(channel);
                        setShowDeleteDialog(true);
                      }}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#dc3545',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer'
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100,
          padding: 16
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: 8,
            padding: 24,
            width: '100%',
            maxWidth: 480,
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{ marginTop: 0, color: '#2a3b5c' }}>
              {editingChannel ? 'Edit Payment Channel' : 'Add Payment Channel'}
            </h3>
            <form onSubmit={handleChannelSubmit}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Name</label>
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g. BPI, GCASH"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4, boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Account Name</label>
                <input
                  name="account_name"
                  value={formData.account_name}
                  onChange={handleInputChange}
                  required
                  placeholder="Account holder name"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4, boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Account / Number</label>
                <input
                  name="value"
                  value={formData.value}
                  onChange={handleInputChange}
                  required
                  placeholder="Account or mobile number"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4, boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Display Order</label>
                <input
                  name="sort_order"
                  type="number"
                  value={formData.sort_order}
                  onChange={handleInputChange}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4, boxSizing: 'border-box' }}
                />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                <input
                  name="is_active"
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                Active (shown to customers)
              </label>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  type="button"
                  onClick={closeForm}
                  style={{
                    padding: '10px 16px',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    backgroundColor: '#fff',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingChannel}
                  style={{
                    padding: '10px 16px',
                    border: 'none',
                    borderRadius: 6,
                    backgroundColor: '#4f8cff',
                    color: '#fff',
                    fontWeight: 600,
                    cursor: savingChannel ? 'not-allowed' : 'pointer',
                    opacity: savingChannel ? 0.6 : 1
                  }}
                >
                  {savingChannel ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteDialog && channelToDelete && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100,
          padding: 16
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: 8,
            padding: 24,
            width: '100%',
            maxWidth: 420,
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{ marginTop: 0, color: '#2a3b5c' }}>Delete Payment Channel?</h3>
            <p style={{ color: '#666' }}>
              Remove <strong>{channelToDelete.name}</strong> ({channelToDelete.value})? This cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={() => {
                  setShowDeleteDialog(false);
                  setChannelToDelete(null);
                }}
                style={{
                  padding: '10px 16px',
                  border: '1px solid #ddd',
                  borderRadius: 6,
                  backgroundColor: '#fff',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  padding: '10px 16px',
                  border: 'none',
                  borderRadius: 6,
                  backgroundColor: '#dc3545',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentSettings;
